import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import Breadcrumbs from "components/common/Breadcrumbs";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import useAsset from "hooks/assets/useAsset";
import useUpdateAsset from "hooks/assets/useUpdateAsset";
import useLocations from "hooks/locations/useLocations";
import useEmployees from "hooks/employees/useEmployees";
import { Asset, type OpenFulfillmentTask } from "types/asset";
import { formSurfaceSx } from "constants/pageSurfaces";
import AssetForm, {
  assetToFormValues,
  AssetFormValues,
  costFromFormForUpdate,
  emptyAssetFormValues,
} from "./AssetForm";
function OpenFulfillmentTasksBanner({ tasks }: { tasks?: OpenFulfillmentTask[] }) {
  if (!tasks?.length) return null;
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Open work order requests for this asset
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {tasks.map((t) => {
          const order = t.orderNumber ?? t.order_number ?? "";
          const tu = t.taskUuid ?? t.task_uuid ?? "";
          const ticketU = t.ticketUuid ?? t.ticket_uuid ?? "";
          const label = t.actionLabel ?? t.action_label ?? t.action ?? "";
          const to = ticketU ? `/work_orders/${ticketU}/edit` : "/work_orders";
          return (
            <Button
              key={tu || `${order}-${label}`}
              component={RouterLink}
              to={to}
              size="small"
              variant="outlined"
            >
              {order} · {label} · {t.status ?? ""}
            </Button>
          );
        })}
      </Box>
    </Alert>
  );
}

function returnStateAfterAssetSave(
  pathWithQuery: string
): { refreshAssets?: boolean } | undefined {
  const path = pathWithQuery.split(/[?#]/)[0];
  if (path === "/assets" || path === "/assets/") {
    return { refreshAssets: true };
  }
  return undefined;
}

export default function AssetDetail() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { asset, isLoading, isError, refetch } = useAsset({ uuid });
  const { update } = useUpdateAsset({ refetch });
  const { locations } = useLocations();
  const { employees } = useEmployees();
  const [form, setForm] = useState<AssetFormValues>(emptyAssetFormValues());
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (asset) {
      setForm(assetToFormValues(asset));
    }
  }, [asset]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset) return;
    setSaveError(null);
    const payload: Asset = {
      ...asset,
      pickup: undefined,
      location: undefined,
      employee: undefined,
      serialNumber: form.serialNumber,
      model: form.model,
      manufacturer: form.manufacturer,
      deviceType: form.deviceType,
      status: form.status,
      condition: form.condition,
      assetTag: form.assetTag,
      unitNumber: form.unitNumber,
      quantity: form.quantity ? Number(form.quantity) : undefined,
      notes: form.notes,
      purchaseDate: form.purchaseDate,
      warrantyExpiresOn: form.warrantyExpiresOn,
      locationId: form.locationId || undefined,
      employeeId: form.employeeId ? Number(form.employeeId) : null,
      ...costFromFormForUpdate(form.cost),
    };
    update(payload)
      .then(() => {
        const from = (location.state as { from?: string } | null)?.from;
        if (
          typeof from === "string" &&
          from.startsWith("/") &&
          !from.startsWith("//")
        ) {
          navigate(from, {
            replace: true,
            state: returnStateAfterAssetSave(from),
          });
        } else {
          navigate("/assets", {
            replace: true,
            state: { refreshAssets: true },
          });
        }
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.errors?.join?.(", ") || "Could not save asset";
        setSaveError(String(msg));
      });
  };

  if (isLoading && !asset) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !asset) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Asset not found.
      </Alert>
    );
  }

  const titleBit = asset.assetTag || asset.serialNumber || asset.uuid;

  return (
    <div>
      <Box>
        <Breadcrumbs
          data={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Assets", to: "/assets" },
            { label: "Edit asset" },
            { label: titleBit },
          ]}
        />
      </Box>
      <Box sx={formSurfaceSx}>
        <Typography variant="h4" mb={1} fontWeight="bold">
          Edit asset
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {titleBit}
        </Typography>
        {asset.pickup?.orderNumber ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Job / pickup: {asset.pickup.orderNumber}
          </Typography>
        ) : null}
        {saveError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        ) : null}
        <OpenFulfillmentTasksBanner tasks={asset.openFulfillmentTasks ?? asset.open_fulfillment_tasks} />
        <Box component="form" onSubmit={handleSubmit}>
          <AssetForm
            values={form}
            setValues={setForm}
            locations={locations}
            employees={employees}
          />
          <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button type="submit" variant="contained">
              Save
            </Button>
            <Button component={RouterLink} to="/assets" variant="outlined">
              Back to list
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  );
}
